import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import type { Screen, LevelCardState, LevelCardEffect } from '../types';
import { LEVEL_DATA } from '../puzzles';
import {
  useGameStore,
  isLevelUnlocked,
  isLevelComplete,
  isSubPuzzleComplete,
} from '../store';
import {
  getDailyLevelIndex,
  isDailyLevelComplete,
  getDailyLevelFirstIncompleteSub,
} from '../dailyLevel';
import { getLatestResumable } from '../savedGameUtils';
import { rateUs } from '@org/rate-us';
import { rateUsConfig } from '../rateUsSettings';
import newLogo from '../../assets/newlogo.png';

import { Muted, Button, Caption, Heading } from './ui';
import { LevelCard } from './LevelCard';
import { ParallaxBg } from './ParallaxBg';
import { Popup } from './Popup';
import { playClick, playPageTurn, ensureAudioContext } from '../sounds';
import { CellState } from '../types';
import './Home.css';

/** Small canvas thumbnail of a saved-game grid showing the user's progress
 *  so far. Filled cells are drawn solid; marks are drawn as faint dots. */
function SavedGamePreview({ grid }: { grid: string[][] }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rows = grid.length;
  const cols = grid[0]?.length ?? rows;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (w === 0 || h === 0) return;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cw = w / cols;
    const ch = h / rows;

    ctx.clearRect(0, 0, w, h);

    // Light background grid for empty cells.
    ctx.fillStyle = 'rgba(192, 128, 16, 0.08)';
    ctx.fillRect(0, 0, w, h);

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = grid[r][c];
        const x = c * cw;
        const y = r * ch;
        if (cell === CellState.Filled) {
          ctx.fillStyle = '#4a2008';
          ctx.fillRect(x, y, cw, ch);
        } else if (cell === CellState.Marked) {
          ctx.fillStyle = 'rgba(74, 32, 8, 0.35)';
          const dot = Math.max(1, Math.min(cw, ch) * 0.25);
          ctx.beginPath();
          ctx.arc(x + cw / 2, y + ch / 2, dot, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
  }, [grid, rows, cols]);

  return (
    <canvas
      ref={canvasRef}
      className="resume-preview-canvas"
      style={{ aspectRatio: `${cols} / ${rows}` }}
    />
  );
}

function useCardsPerPage() {
  const [cpp, setCpp] = useState(() => window.innerWidth <= 500 ? 2 : 3);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 500px)');
    const handler = (e: MediaQueryListEvent) => setCpp(e.matches ? 2 : 3);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return cpp;
}

interface HomeProps {
  onNavigate: (screen: Screen) => void;
}

export function Home({ onNavigate }: HomeProps) {
  const completions = useGameStore(s => s.completions);
  const savedGames = useGameStore(s => s.savedGames);
  const trainSettings = useGameStore(s => s.trainSettings);
  const difficulty = trainSettings.difficulty;
  const PROGRESSION_LEVELS = LEVEL_DATA.progression[difficulty];

  const [resumePromptOpen, setResumePromptOpen] = useState(false);
  const [rateUsState, setRateUsState] = useState<
    'initial' | 'rating' | 'thanks' | 'hidden'
  >('initial');
  const thanksTimerRef = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (thanksTimerRef.current !== null) {
        window.clearTimeout(thanksTimerRef.current);
      }
    };
  }, []);

  const CARDS_PER_PAGE = useCardsPerPage();
  const totalItems = PROGRESSION_LEVELS.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / CARDS_PER_PAGE));

  // First unlocked-but-incomplete campaign level.
  const nextLevelIndex = useMemo(() => {
    for (let i = 0; i < PROGRESSION_LEVELS.length; i++) {
      const prevId = i > 0 ? PROGRESSION_LEVELS[i - 1].id : undefined;
      if (
        isLevelUnlocked(completions, i, prevId) &&
        !isLevelComplete(completions, PROGRESSION_LEVELS[i].id)
      ) {
        return i;
      }
    }
    return -1;
  }, [completions, PROGRESSION_LEVELS]);

  const initialPage = useMemo(() => {
    if (nextLevelIndex >= 0) return Math.floor(nextLevelIndex / CARDS_PER_PAGE);
    return Math.max(0, totalPages - 1);
  }, [nextLevelIndex, CARDS_PER_PAGE, totalPages]);

  const [page, setPage] = useState(initialPage);
  const scrollRef = useRef<HTMLDivElement>(null);
  const programmaticScroll = useRef(false);

  const safePage = Math.min(page, totalPages - 1);

  // Auto-scroll to the correct page on mount
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    programmaticScroll.current = true;
    let attempts = 0;
    const tryScroll = () => {
      if (!el.clientWidth || attempts++ > 20) {
        programmaticScroll.current = false;
        return;
      }
      const target = initialPage * el.clientWidth;
      el.style.scrollSnapType = 'none';
      el.scrollLeft = target;
      if (Math.abs(el.scrollLeft - target) < 2) {
        setPage(initialPage);
        requestAnimationFrame(() => {
          el.style.scrollSnapType = '';
          setTimeout(() => { programmaticScroll.current = false; }, 100);
        });
      } else {
        requestAnimationFrame(tryScroll);
      }
    };
    requestAnimationFrame(tryScroll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScroll = useCallback(() => {
    if (programmaticScroll.current) return;
    const el = scrollRef.current;
    if (!el || el.clientWidth === 0) return;
    const newPage = Math.round(el.scrollLeft / el.clientWidth);
    setPage(Math.max(0, Math.min(newPage, totalPages - 1)));
  }, [totalPages]);

  const scrollToPage = useCallback((p: number) => {
    const el = scrollRef.current;
    if (!el) return;
    playPageTurn();
    programmaticScroll.current = true;
    el.scrollTo({ left: p * el.clientWidth, behavior: 'smooth' });
    setPage(p);
    setTimeout(() => { programmaticScroll.current = false; }, 400);
  }, []);

  const allLevelsComplete = useMemo(
    () => PROGRESSION_LEVELS.every(l => isLevelComplete(completions, l.id)),
    [completions, PROGRESSION_LEVELS],
  );

  // Daily level state — recomputed on every render (cheap; date-indexed).
  const dailyIndex = getDailyLevelIndex(difficulty);
  const dailyDone = isDailyLevelComplete(completions, difficulty);

  // Most-recent resumable save across all categories (levels/daily, train,
  // shared). Null when there's nothing to continue.
  const latestResumable = useMemo(
    () => getLatestResumable(savedGames, dailyIndex, trainSettings),
    [savedGames, dailyIndex, trainSettings],
  );

  // Whether the user has ever played a puzzle (completed or in-progress).
  const hasPlayedAny = useMemo(
    () =>
      Object.keys(completions).length > 0 ||
      Object.keys(savedGames).length > 0,
    [completions, savedGames],
  );

  const handleOpenHistory = useCallback(() => {
    ensureAudioContext();
    playClick();
    onNavigate({ type: 'history' });
  }, [onNavigate]);

  const handleOpenRateUs = useCallback(() => {
    ensureAudioContext();
    playClick();
    setRateUsState('rating');
  }, []);

  const handleRateStars = useCallback((stars: number) => {
    playClick();
    if (stars >= 4) {
      rateUs(rateUsConfig);
      setRateUsState('hidden');
      return;
    }
    setRateUsState('thanks');
    if (thanksTimerRef.current !== null) {
      window.clearTimeout(thanksTimerRef.current);
    }
    thanksTimerRef.current = window.setTimeout(() => {
      setRateUsState('hidden');
      thanksTimerRef.current = null;
    }, 1500);
  }, []);

  const handleOpenResumePrompt = useCallback(() => {
    ensureAudioContext();
    playClick();
    if (!latestResumable) return;
    setResumePromptOpen(true);
  }, [latestResumable]);

  const handleConfirmResume = useCallback(() => {
    setResumePromptOpen(false);
    if (!latestResumable) return;
    onNavigate(latestResumable.screen);
  }, [latestResumable, onNavigate]);

  const handleRestart = useCallback(() => {
    setResumePromptOpen(false);
    if (!latestResumable) return;
    useGameStore.getState().clearGameState(latestResumable.key);
    onNavigate({ ...latestResumable.screen, entry: 'fresh' } as Screen);
  }, [latestResumable, onNavigate]);

  const handleCancelResume = useCallback(() => {
    setResumePromptOpen(false);
  }, []);

  // Square "Play" button — always starts fresh (never resumes). Prefers the
  // daily puzzle if available, then the next unlocked campaign level.
  const handlePlay = useCallback(() => {
    ensureAudioContext();
    playClick();

    // Daily level first.
    if (!dailyDone && dailyIndex >= 0) {
      const subIndex = Math.max(
        0,
        getDailyLevelFirstIncompleteSub(completions, difficulty),
      );
      onNavigate({
        type: 'level',
        mode: 'multi-sub',
        track: 'daily',
        difficulty,
        levelIndex: dailyIndex,
        subIndex,
        entry: 'fresh',
      });
      return;
    }

    // Fall back to next progression level.
    if (nextLevelIndex < 0) return;
    const level = PROGRESSION_LEVELS[nextLevelIndex];
    for (let i = 0; i < level.puzzles.length; i++) {
      if (!isSubPuzzleComplete(completions, level.id, level.puzzles[i].id)) {
        onNavigate({
          type: 'level',
          mode: 'multi-sub',
          track: 'progression',
          difficulty,
          levelIndex: nextLevelIndex,
          subIndex: i,
          entry: 'fresh',
        });
        return;
      }
    }
    onNavigate({
      type: 'level',
      mode: 'multi-sub',
      track: 'progression',
      difficulty,
      levelIndex: nextLevelIndex,
      subIndex: 0,
      entry: 'fresh',
    });
  }, [completions, dailyDone, dailyIndex, difficulty, nextLevelIndex, PROGRESSION_LEVELS, onNavigate]);

  const handlePlaySub = useCallback((levelIndex: number, subIndex: number) => {
    ensureAudioContext();
    playClick();
    onNavigate({
      type: 'level',
      mode: 'multi-sub',
      track: 'progression',
      difficulty,
      levelIndex,
      subIndex,
      entry: 'fresh',
    });
  }, [difficulty, onNavigate]);

  // Build pages of campaign level indices.
  const pages = useMemo(() => {
    const items = PROGRESSION_LEVELS.map((_, i) => i);
    const result: number[][] = [];
    for (let i = 0; i < items.length; i += CARDS_PER_PAGE) {
      result.push(items.slice(i, i + CARDS_PER_PAGE));
    }
    return result.length > 0 ? result : [[]];
  }, [CARDS_PER_PAGE, PROGRESSION_LEVELS]);

  // Play button meta — daily level promoted until it's done. Always labeled
  // "Play" (never "Continue"); resume lives on the dedicated Continue button
  // in the quick-links row below.
  const playButtonDisabled = dailyDone && allLevelsComplete;
  const playButtonLabel = playButtonDisabled ? '\u2714' : 'Play';
  const playButtonMeta = (() => {
    if (!dailyDone && dailyIndex >= 0) return 'Level of the day';
    if (allLevelsComplete) return 'All done!';
    if (nextLevelIndex >= 0) return `Level ${nextLevelIndex + 1}`;
    return 'Play';
  })();

  return (
    <div className="home-layout">
    <div className="home-container">
      <ParallaxBg />

      <img className="home-logo" src={newLogo} alt="Nonogram Daily" draggable={false} />

      <div className="home-buttons">
        <Button variant="primary" size="square" className="home-btn" onClick={() => { ensureAudioContext(); playClick(); onNavigate({ type: 'setup' }); }}>
          <span className="home-btn-label">Setup</span>
          <Muted className="home-btn-meta">Rules & difficulty</Muted>
        </Button>

        <Button
          variant={playButtonDisabled ? 'secondary' : 'primary'}
          size="square"
          className="home-btn"
          onClick={handlePlay}
          disabled={playButtonDisabled}
        >
          <span className="home-btn-label">{playButtonLabel}</span>
          <Muted className="home-btn-meta">{playButtonMeta}</Muted>
        </Button>

        <Button variant="primary" size="square" className="home-btn" onClick={() => { ensureAudioContext(); playClick(); onNavigate({ type: 'lab-setup' }); }}>
          <span className="home-btn-label">Practice</span>
          <Muted className="home-btn-meta">Infinite puzzles</Muted>
        </Button>

        <Button variant="primary" size="square" className="home-btn" onClick={() => { ensureAudioContext(); playClick(); onNavigate({ type: 'creator' }); }}>
          <span className="home-btn-label">Create</span>
          <Muted className="home-btn-meta">Design puzzles</Muted>
        </Button>

      </div>

      <div className="home-links" role="group" aria-label="Quick links">
        <Button
          variant="ghost"
          size="wide-sm"
          className={`home-link-btn home-link-btn--fit${latestResumable ? '' : ' home-link-btn--hidden'}`}
          onClick={handleOpenResumePrompt}
        >
          <span className="home-link-btn__text">
            {latestResumable ? `Continue ${latestResumable.label}` : 'Continue'}
          </span>
        </Button>
        <Button
          variant="ghost"
          size="wide-sm"
          className={`home-link-btn home-link-btn--fit${hasPlayedAny ? '' : ' home-link-btn--hidden'}`}
          onClick={handleOpenHistory}
        >
          <span className="home-link-btn__text">History</span>
        </Button>
        {rateUsState === 'initial' && (
          <Button
            variant="ghost"
            size="wide-sm"
            className="home-link-btn home-link-btn--fit"
            onClick={handleOpenRateUs}
          >
            <span className="home-link-btn__text">Rate us</span>
          </Button>
        )}
        {rateUsState === 'rating' && (
          <div
            className="home-rate-stars"
            role="radiogroup"
            aria-label="Rate the game from 1 to 5 stars"
          >
            {/* Rendered in reverse so that `flex-direction: row-reverse` in the
                CSS flips them back to visual 1..5 left-to-right, while the
                `:hover ~ .home-rate-star` sibling combinator correctly lights
                up every star up to and including the hovered one. */}
            {[5, 4, 3, 2, 1].map((n) => (
              <button
                key={n}
                type="button"
                className="home-rate-star"
                onClick={() => handleRateStars(n)}
                aria-label={`${n} ${n === 1 ? 'star' : 'stars'}`}
              >
                ★
              </button>
            ))}
          </div>
        )}
        {rateUsState === 'thanks' && (
          <span className="home-rate-thanks" role="status">
            Thanks!
          </span>
        )}
      </div>

      <div className="home-carousel-section">
        <div className="home-carousel" ref={scrollRef} onScroll={handleScroll}>
          {pages.map((pageIndices, pi) => (
            <div className="home-carousel-page" key={pi}>
              {pageIndices.map((idx) => {
                const prevId = idx > 0 ? PROGRESSION_LEVELS[idx - 1].id : undefined;
                const unlocked = isLevelUnlocked(completions, idx, prevId);
                const done = isLevelComplete(completions, PROGRESSION_LEVELS[idx].id);
                const cardState: LevelCardState = !unlocked
                  ? 'locked'
                  : done
                    ? 'completed'
                    : 'in-progress';
                const cardEffects: LevelCardEffect[] | undefined =
                  idx === nextLevelIndex ? ['glow'] : undefined;
                return (
                  <div className="home-card-wrap" key={idx}>
                    <Caption className="home-card-label">Level {idx + 1}</Caption>
                    <LevelCard
                      level={PROGRESSION_LEVELS[idx]}
                      levelIndex={idx}
                      state={cardState}
                      effects={cardEffects}
                      completions={completions}
                      onPlaySub={handlePlaySub}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="home-levels-pagination">
          <Button
            variant="ghost"
            size="circle"
            className="home-page-arrow"
            onClick={() => scrollToPage(Math.max(0, safePage - 1))}
            disabled={safePage === 0}
            aria-label="Previous page"
          >
            &#x2039;
          </Button>
          <Caption className="home-page-indicator">{safePage + 1} / {totalPages}</Caption>
          <Button
            variant="ghost"
            size="circle"
            className="home-page-arrow"
            onClick={() => scrollToPage(Math.min(totalPages - 1, safePage + 1))}
            disabled={safePage === totalPages - 1}
            aria-label="Next page"
          >
            &#x203A;
          </Button>
        </div>
      </div>

    </div>
    {resumePromptOpen && latestResumable && (
      <Popup
        aria-label="Resume puzzle?"
        onDismiss={handleCancelResume}
        maxWidth={340}
      >
        <div className="resume-dialog">
          <div className="resume-dialog__topbar">
            <Heading className="resume-dialog__title">Resume puzzle?</Heading>
            <Button
              variant="secondary"
              size="circle"
              onClick={handleCancelResume}
              aria-label="Close"
            >
              {'\u2715'}
            </Button>
          </div>
          <p className="resume-dialog__label">{latestResumable.label}</p>
          {savedGames[latestResumable.key]?.grid && (
            <div className="resume-dialog__preview">
              <SavedGamePreview grid={savedGames[latestResumable.key].grid} />
            </div>
          )}
          <div className="resume-dialog__buttons">
            <Button variant="primary" size="wide-sm" onClick={handleConfirmResume}>
              Resume
            </Button>
            <Button variant="ghost" size="wide-sm" onClick={handleRestart}>
              Restart
            </Button>
          </div>
        </div>
      </Popup>
    )}
    </div>
  );
}
